import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
  EditorState,
  LexicalNode,
} from 'lexical';
import { InlineBoxType, MarkResult } from '$/components/InlineBox/types';
import { EditorSerializedContent } from './types';
import {
  $createInlineBoxNode,
  $createMentionNode,
  $isInlineBoxNode,
  $isMentionNode,
} from './nodes';
import {
  getPostTaskCaretTextOffset,
  isPostTaskInlineBoxData,
  mergePostTaskAdjacentText,
  normalizePostTaskCaretText,
  POST_TASK_CARET_ANCHOR,
  serializePostTaskCaretText,
  serializePostTaskLeadingCaretText,
} from './postTaskLabel';

/** 整段重写编辑区内容时传入 editor.update tags，避免误触发 InlineBox 的 onRemove */
export const INLINE_BOX_BULK_REPLACE_TAG = 'inline-box-bulk-replace';

/** WebKit 在 contenteditable=false 原子节点边界更容易把光标画进节点内部，用零宽锚点承接真实插入位。 */
const INLINE_DECORATOR_TEXT_ANCHOR = '\u200B';

/** 文本片段入结构化结果前，去掉普通装饰节点的零宽锚点，避免污染业务内容。 */
const sanitizeDecoratorAnchorText = (text: string) => text.replace(/\u200B/g, '');

/** 取文本节点开头连续零宽锚点长度，用于把光标放到“原子节点之后”的真实可编辑位置。 */
export const getTextNodeCaretStartOffset = (node: LexicalNode | null | undefined) => {
  if (!$isTextNode(node)) return 0;

  const previousSibling = node.getPreviousSibling();
  if ($isInlineBoxNode(previousSibling) && isPostTaskInlineBoxData(previousSibling.__inlineBox.data)) {
    return getPostTaskCaretTextOffset(node.getTextContent());
  }

  const text = node.getTextContent();
  let offset = 0;
  while (text[offset] === INLINE_DECORATOR_TEXT_ANCHOR) {
    offset += 1;
  }
  return offset;
};

/** 合并纯文本片段时过滤内部零宽锚点，避免光标承接字符进入提交结果。 */
const mergeTextFragment = (result: EditorSerializedContent, text: string) => {
  const sanitizedText = sanitizeDecoratorAnchorText(text);
  if (!sanitizedText) return;
  const last = result[result.length - 1];
  if (last && last.type === 'text') {
    last.value += sanitizedText;
    return;
  }
  result.push({ type: 'text', value: sanitizedText });
};

/** Mention / InlineBox 等行内装饰两侧补空 TextNode，避免原生选区贴在 contenteditable=false 边界时光标画在 chip 前 */
const ensureInlineDecoratorTextAnchor = (decoratorNode: LexicalNode, direction: 'before' | 'after') => {
  const sibling = direction === 'before'
    ? decoratorNode.getPreviousSibling()
    : decoratorNode.getNextSibling();

  if ($isTextNode(sibling)) {
    if (!sibling.getTextContent()) {
      sibling.setTextContent(INLINE_DECORATOR_TEXT_ANCHOR);
    }
    return sibling;
  }

  const anchorNode = $createTextNode(INLINE_DECORATOR_TEXT_ANCHOR);
  if (direction === 'before') {
    decoratorNode.insertBefore(anchorNode);
  } else {
    decoratorNode.insertAfter(anchorNode);
  }

  return anchorNode;
};

/**
 * 把 post_task 固定到整段富文本最前面，并只在标签后保留光标锚点。
 * 历史数据若在标签前已有文本，会按原文顺序移动到标签后。
 */
const ensurePostTaskTextAnchors = (decoratorNode: LexicalNode) => {
  const root = $getRoot();
  const firstRootChild = root.getFirstChild();
  const targetParent = $isElementNode(firstRootChild)
    ? firstRootChild
    : decoratorNode.getParent();
  if (!$isElementNode(targetParent)) return null;

  const previousSibling = decoratorNode.getPreviousSibling();
  const targetFirstChild = targetParent.getFirstChild();
  const needsMove = decoratorNode.getParent() !== targetParent
    || decoratorNode !== targetFirstChild;

  if (needsMove) {
    const oldNextSibling = decoratorNode.getNextSibling();
    if ($isTextNode(previousSibling)) {
      const leadingText = previousSibling.getTextContent();
      const nextText = $isTextNode(oldNextSibling) ? oldNextSibling.getTextContent() : '';
      const mergedText = mergePostTaskAdjacentText(leadingText, nextText);
      if (mergedText) {
        previousSibling.setTextContent(mergedText);
      } else {
        previousSibling.remove();
      }
      if ($isTextNode(oldNextSibling)) {
        oldNextSibling.remove();
      }
    } else if ($isTextNode(oldNextSibling)) {
      const trailingText = serializePostTaskCaretText(oldNextSibling.getTextContent());
      if (trailingText) {
        oldNextSibling.setTextContent(trailingText);
      } else {
        oldNextSibling.remove();
      }
    }

    const nextTargetFirstChild = targetParent.getFirstChild();
    if (nextTargetFirstChild) {
      nextTargetFirstChild.insertBefore(decoratorNode);
    } else {
      targetParent.append(decoratorNode);
    }
  }

  const nextSibling = decoratorNode.getNextSibling();
  if ($isTextNode(nextSibling)) {
    const nextText = normalizePostTaskCaretText(nextSibling.getTextContent());
    if (nextText !== nextSibling.getTextContent()) {
      nextSibling.setTextContent(nextText);
    }
    return nextSibling;
  }

  const anchorNode = $createTextNode(POST_TASK_CARET_ANCHOR);
  decoratorNode.insertAfter(anchorNode);
  return anchorNode;
};

/** 把 Lexical EditorState 序列化为业务可持久化内容时，忽略仅用于 WebKit 光标修正的锚点字符。 */
export const serializeEditorStateToContent = (
  editorState: EditorState,
  markResultsMap?: Map<string, MarkResult[]>,
): EditorSerializedContent => {
  const result: EditorSerializedContent = [];

  editorState.read(() => {
    const root = $getRoot();
    const children = root.getChildren();
    let shouldSerializePostTaskCaretText = false;

    children.forEach((child, index) => {
      const traverse = (node: LexicalNode) => {
        if ($isMentionNode(node)) {
          shouldSerializePostTaskCaretText = false;
          const mention = node.getMention();
          result.push({
            type: 'mention',
            value: {
              id: mention.id,
              name: mention.name,
            },
          });
          return;
        }
        if ($isTextNode(node)) {
          const nextSibling = node.getNextSibling();
          const isPostTaskLeadingCaretText = (
            $isInlineBoxNode(nextSibling)
            && isPostTaskInlineBoxData(nextSibling.__inlineBox.data)
          );
          let text = node.getTextContent();
          if (shouldSerializePostTaskCaretText) {
            text = serializePostTaskCaretText(text);
          } else if (isPostTaskLeadingCaretText) {
            text = serializePostTaskLeadingCaretText(text);
          }
          shouldSerializePostTaskCaretText = false;
          mergeTextFragment(result, text);
          return;
        }
        if ($isLineBreakNode(node)) {
          shouldSerializePostTaskCaretText = false;
          mergeTextFragment(result, '\n');
          return;
        }
        if ($isInlineBoxNode(node)) {
          shouldSerializePostTaskCaretText = isPostTaskInlineBoxData(node.__inlineBox.data);
          const fragment: EditorSerializedContent[number] = { type: 'inline-box', value: node.__inlineBox };
          const nodeResultList = markResultsMap?.get(node.getKey());
          if (nodeResultList?.length) {
            (fragment as { resultList?: MarkResult[]; }).resultList = nodeResultList;
          }
          result.push(fragment);
          return;
        }
        if ($isElementNode(node)) {
          node.getChildren().forEach(traverse);
        }
      };

      traverse(child);

      if ($isParagraphNode(child) && index < children.length - 1) {
        mergeTextFragment(result, '\n');
      }
    });
  });

  return result;
};

/** 结构化内容转纯文本时沿用过滤后的文本值，保证长度统计与提交文案不包含内部锚点。 */
export const getPlainTextFromContent = (content: EditorSerializedContent): string => {
  return content
    .map((item) => {
      if (item.type === 'text') return sanitizeDecoratorAnchorText(item.value);
      if (item.type === 'mention') return `@${item.value.name}`;
      return '';
    })
    .join('');
};

/** 输入长度限制按用户可见文本计算，不把 Safari 选区锚点算进长度。 */
export const getEditorTextLength = (editorState: EditorState): number => {
  const content = serializeEditorStateToContent(editorState);
  return getPlainTextFromContent(content).length;
};

/** 外部值重建编辑器时，同步补回原子节点两侧零宽锚点；可按需跳过末尾选区恢复，避免未聚焦实例依赖 focus 才显示。 */
// [DEBUG: Input回显] 编辑器重建函数
export const buildEditorFromContent = (
  content: EditorSerializedContent,
  options?: { selectEnd?: boolean; },
): Map<string, MarkResult[]> => {
  const root = $getRoot();
  root.clear();

  const restoredResults = new Map<string, MarkResult[]>();
  const paragraph = $createParagraphNode();
  root.append(paragraph);

  content.forEach((fragment) => {
    if (fragment.type === 'inline-box') {
      const node = $createInlineBoxNode(fragment.value);
      paragraph.append(node);
      if (fragment.resultList?.length) {
        restoredResults.set(node.getKey(), fragment.resultList);
      }
      return;
    }
    if (fragment.type === 'mention') {
      paragraph.append($createMentionNode(fragment.value));
      return;
    }
    const parts = sanitizeDecoratorAnchorText(fragment.value)
      .split('\n');
    parts.forEach((part, index) => {
      if (part) {
        paragraph.append($createTextNode(part));
      }
      if (index < parts.length - 1) {
        paragraph.append($createLineBreakNode());
      }
    });
  });

  normalizeMentionTextAnchors();

  // 重建后将光标置于末尾
  if (options?.selectEnd !== false) {
    paragraph.selectEnd();
  }

  return restoredResults;
};

/** 文本插入维持 Lexical 默认行为，选区锚点字符在序列化阶段统一剥离。 */
export const insertTextAtSelection = (text: string) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return false;

  // Lexical 的 selection.insertText 不会将 \n 转换为 LineBreakNode，
  // 只会作为普通字符插入。需要手动按 \n 拆分，逐段插入文本和换行节点。
  if (!text.includes('\n')) {
    selection.insertText(text);
    return true;
  }

  const parts = text.split('\n');
  parts.forEach((part, index) => {
    if (part) {
      selection.insertText(part);
    }
    if (index < parts.length - 1) {
      selection.insertNodes([$createLineBreakNode()]);
    }
  });
  return true;
};

export const syncMarkInlineBoxOrder = () => {
  const root = $getRoot();
  const nodes: LexicalNode[] = [];
  const collect = (node: LexicalNode) => {
    nodes.push(node);
    if ($isElementNode(node)) {
      node.getChildren().forEach(collect);
    }
  };

  root.getChildren().forEach(collect);
  let orderIndex = 0;
  nodes.forEach((node) => {
    if ($isInlineBoxNode(node) && node.__inlineBox?.type === InlineBoxType.Mark) {
      const nextOrder = orderIndex + 1;
      if (node.__inlineBox.data.order !== nextOrder) {
        const writable = node.getWritable();
        writable.__inlineBox = {
          ...writable.__inlineBox,
          data: {
            ...writable.__inlineBox.data,
            order: nextOrder,
          },
        };
      }
      orderIndex += 1;
    }
  });
};

/** 遍历行内装饰节点并补齐光标锚点；post_task 额外固定在富文本最前面。 */
export const normalizeMentionTextAnchors = () => {
  const root = $getRoot();

  const visit = (node: LexicalNode) => {
    if (!$isElementNode(node)) return;

    node.getChildren().forEach((child) => {
      if ($isMentionNode(child) || $isInlineBoxNode(child)) {
        if ($isInlineBoxNode(child) && isPostTaskInlineBoxData(child.__inlineBox.data)) {
          ensurePostTaskTextAnchors(child);
          return;
        }
        ensureInlineDecoratorTextAnchor(child, 'before');
        ensureInlineDecoratorTextAnchor(child, 'after');
        return;
      }

      visit(child);
    });
  };

  visit(root);
};
